import { useEffect, useState } from "react";

import {
  DashboardFilterType,
  GetBuyerTypeMarginsItem,
} from "@/types/dashboard";

import { getDashboardProjectFilterToString } from "@/consts/Dashboard";

import { formatToPercentString, formatKoreanCurrencyWithWon } from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";
import { downloadExcel, getExcelFileName } from "@/utils/excelUtils";

import { GetBuyerTypeMargins } from "@/services/Dashboard";

import { captureDashboardSearch } from "@/libs/dashboardSearch";

import DashboardCardHeader from "@/components/DashboardCardHeader";

type Props = {
  title: string;
  searchReady: boolean;
  filter: DashboardFilterType;
  onSearchComplete: () => void;
};

export default function DashboardDivisionMargin({
  title,
  searchReady,
  filter,
  onSearchComplete,
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<GetBuyerTypeMarginsItem[]>([]);

  const onLoad = async () => {
    const search = captureDashboardSearch();
    try {
      setIsLoading(true);
      const params = getDashboardProjectFilterToString(filter);
      const result = await GetBuyerTypeMargins(params);
      if (!search.isCurrent()) return;

      if (result.data.success) {
        setData(result.data.pods[0].content.items);
      } else {
        showToast({
          type: "error",
          message: `${title}을(를) 조회하는데 실패했습니다. ${result.data.error?.type}`,
        });
      }
    } catch (e) {
      if (!search.isCurrent()) return;
      showToast({
        type: "error",
        message: `${title}을(를) 조회하는데 실패했습니다. ${e}`,
      });
    } finally {
      if (search.isCurrent()) {
        setIsLoading(false);
        onSearchComplete();
      }
    }
  };

  useEffect(() => {
    if (searchReady) {
      onLoad();
    }
  }, [searchReady]);

  const handleExcelDownload = () => {
    if (data.length === 0) {
      showToast({ type: "error", message: "다운로드할 데이터가 없습니다." });
      return;
    }

    const columns = [
      { header: "디비전", key: "buyer_type", width: 20 },
      { header: "총 판매가", key: "total_sales_amount", width: 20 },
      { header: "총 마진", key: "margin_rate_display", width: 15 },
    ];

    const excelData = data.map((item) => ({
      ...item,
      margin_rate_display: formatToPercentString(item.margin_rate, 2),
    }));

    downloadExcel(excelData, columns, getExcelFileName("디비전별_마진_금액"));
  };

  return (
    <DashboardCardHeader
      title={title}
      isLoading={isLoading}
      rightElement={
        <button
          className="flex items-center gap-1 text-xs text-gray-A hover:text-gray-600"
          onClick={handleExcelDownload}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4.66666 6.66669L7.99999 10L11.3333 6.66669"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 10V2"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          EXCEL 다운로드
        </button>
      }
    >
      <div className="-mx-7 -mb-5">
        <table className="w-full text-sm">
          <thead className="bg-dark-red-60 text-white text-left text-sm font-semibold sticky top-0">
            <tr>
              <th className="px-4 py-2">디비전</th>
              <th className="px-4 py-2 text-right">총 판매가</th>
              <th className="px-4 py-2 text-right">총 마진</th>
            </tr>
          </thead>
          <tbody className="divide-y text-gray-A text-xs font-normal">
            {data.map((item, index) => (
              <tr key={index} className="hover:bg-gray-E">
                <td className="px-4 py-2">{item.buyer_type}</td>
                <td className="px-4 py-2 text-right">
                  {formatKoreanCurrencyWithWon(item.total_sales_amount)}
                </td>
                <td className="px-4 py-2 text-right">
                  {formatToPercentString(item.margin_rate, 2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardCardHeader>
  );
}
